import React, { useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  MaterialAppSource,
  MaterialEngagementSeriesPoint,
  ProjectMaterialDailyPoint,
} from '../../services/api';
import { chartCardSx } from './analyticsChartUi';
import { ChartDownloadImageButton } from './ChartDownloadImageButton';

type MaterialDailySeriesPoint = ProjectMaterialDailyPoint | MaterialEngagementSeriesPoint;

function dailyEdmPreviews(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEdmPreviews;
  if (source === 'project_essential') return p.peEdmPreviews;
  return p.spEdmPreviews + p.peEdmPreviews;
}

function dailyEdmDownloads(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEdmDownloads;
  if (source === 'project_essential') return p.peEdmDownloads;
  return p.spEdmDownloads + p.peEdmDownloads;
}

function dailyEbookPreviews(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEbookPreviews;
  if (source === 'project_essential') return p.peEbookPreviews;
  return p.spEbookPreviews + p.peEbookPreviews;
}

function dailyEbookDownloads(p: MaterialDailySeriesPoint, source: MaterialAppSource): number {
  if (source === 'super_picker') return p.spEbookDownloads;
  if (source === 'project_essential') return p.peEbookDownloads;
  return p.spEbookDownloads + p.peEbookDownloads;
}

const materialToggleGroupSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.25,
    py: 0.35,
    fontSize: '0.75rem',
    borderColor: 'rgba(var(--color-text-rgb), 0.18)',
    color: 'rgba(var(--color-text-rgb), 0.85)',
    '&.Mui-selected': {
      bgcolor: 'rgba(var(--color-accent-rgb), 0.16)',
      color: 'var(--color-accent)',
      borderColor: 'rgba(var(--color-accent-rgb), 0.35)',
      '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.22)' },
    },
  },
} as const;

const MaterialFilterGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(var(--color-text-rgb), 0.65)',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

export const MaterialAnalyticsToolbar: React.FC<{
  title: string;
  materialAppSource: MaterialAppSource;
  onMaterialAppSourceChange: (source: MaterialAppSource) => void;
  onRefresh: () => void;
  metric?: 'previews' | 'downloads';
  onMetricChange?: (metric: 'previews' | 'downloads') => void;
  materialType?: 'edm' | 'ebook';
  onMaterialTypeChange?: (type: 'edm' | 'ebook') => void;
  showSourceToggle?: boolean;
  downloadSlot?: React.ReactNode;
}> = ({
  title,
  materialAppSource,
  onMaterialAppSourceChange,
  onRefresh,
  metric,
  onMetricChange,
  materialType,
  onMaterialTypeChange,
  showSourceToggle = true,
  downloadSlot,
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {downloadSlot}
        <Button
          data-html2canvas-ignore="true"
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{
            flexShrink: 0,
            borderColor: 'rgba(var(--color-text-rgb), 0.18)',
            color: 'rgba(var(--color-text-rgb), 0.88)',
          }}
        >
          Refresh
        </Button>
      </Box>
    </Box>
    <Box
      data-html2canvas-ignore="true"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 2,
        p: 1.25,
        borderRadius: 1,
        border: '1px solid rgba(var(--color-text-rgb), 0.1)',
        bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
      }}
    >
      {showSourceToggle ? (
        <MaterialFilterGroup label="Source">
          <ToggleButtonGroup
            value={materialAppSource}
            exclusive
            onChange={(_, val) => { if (val) onMaterialAppSourceChange(val as MaterialAppSource); }}
            size="small"
            sx={materialToggleGroupSx}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="super_picker">Super Picker</ToggleButton>
            <ToggleButton value="project_essential">Project Essentials</ToggleButton>
          </ToggleButtonGroup>
        </MaterialFilterGroup>
      ) : null}
      {metric && onMetricChange ? (
        <MaterialFilterGroup label="Activity">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, val) => { if (val) onMetricChange(val); }}
            sx={materialToggleGroupSx}
          >
            <ToggleButton value="previews">Previews</ToggleButton>
            <ToggleButton value="downloads">Downloads</ToggleButton>
          </ToggleButtonGroup>
        </MaterialFilterGroup>
      ) : null}
      {materialType && onMaterialTypeChange ? (
        <MaterialFilterGroup label="Material">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={materialType}
            onChange={(_, val) => { if (val) onMaterialTypeChange(val); }}
            sx={materialToggleGroupSx}
          >
            <ToggleButton value="edm">EDM</ToggleButton>
            <ToggleButton value="ebook">eBook</ToggleButton>
          </ToggleButtonGroup>
        </MaterialFilterGroup>
      ) : null}
    </Box>
  </Box>
);

const MaterialActivityLineChart: React.FC<{
  title: string;
  loading: boolean;
  error?: string;
  series: MaterialDailySeriesPoint[];
  metric: 'previews' | 'downloads';
  onMetricChange: (metric: 'previews' | 'downloads') => void;
  materialAppSource: MaterialAppSource;
  onRefresh: () => void;
  showSourceToggle?: boolean;
  onMaterialAppSourceChange?: (source: MaterialAppSource) => void;
  chartGridStroke: string;
  chartAxisStroke: string;
  chartTickStyle: { fill: string; fontSize: number };
  downloadFileName?: string;
}> = ({
  title,
  loading,
  error,
  series,
  metric,
  onMetricChange,
  materialAppSource,
  onRefresh,
  showSourceToggle = false,
  onMaterialAppSourceChange,
  chartGridStroke,
  chartAxisStroke,
  chartTickStyle,
  downloadFileName,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const chartData = React.useMemo(
    () =>
      series.map((p) => ({
        label: p.date,
        edm: metric === 'previews'
          ? dailyEdmPreviews(p, materialAppSource)
          : dailyEdmDownloads(p, materialAppSource),
        ebook: metric === 'previews'
          ? dailyEbookPreviews(p, materialAppSource)
          : dailyEbookDownloads(p, materialAppSource),
      })),
    [series, metric, materialAppSource],
  );

  return (
    <Box ref={cardRef} sx={chartCardSx}>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <MaterialAnalyticsToolbar
        title={title}
        materialAppSource={materialAppSource}
        onMaterialAppSourceChange={onMaterialAppSourceChange ?? (() => {})}
        metric={metric}
        onMetricChange={onMetricChange}
        onRefresh={onRefresh}
        showSourceToggle={showSourceToggle}
        downloadSlot={
          downloadFileName
            ? <ChartDownloadImageButton targetRef={cardRef} fileName={downloadFileName} />
            : undefined
        }
      />
      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : chartData.every((d) => d.edm === 0 && d.ebook === 0) ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
          No material activity in this period.
        </Typography>
      ) : (
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid stroke={chartGridStroke} strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke={chartAxisStroke} tick={chartTickStyle} />
              <YAxis allowDecimals={false} stroke={chartAxisStroke} tick={chartTickStyle} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ color: 'var(--color-text-strong)' }} />
              <Line type="monotone" dataKey="edm" name="EDM" stroke="#4299E1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="ebook" name="eBook" stroke="#ED8936" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default MaterialActivityLineChart;
